// src/types/admin.ts — record shapes shared by the admin dashboard shell
// (pages/AdminDashboard.tsx) and the Talent / Revenue / Admins tabs and
// modals it renders. Moved out of AdminDashboard.tsx in the Phase C split.
// The Feedback and Fans tabs' records live at the end.
import { CurrencyTotals } from "../utils/currency";

// Talent-tab chip filter. Lives in the dashboard shell (not the tab) because
// the Growth tab's "needs bank" link sets it before switching tabs.
export type TalentFilter = "all" | "ready" | "waitingApproval" | "needsBank" | "frozen";
export const TALENT_FILTERS: readonly TalentFilter[] = ["all", "ready", "waitingApproval", "needsBank", "frozen"];

// Where a talent stands on the way to paid requests (server
// lib/talentMoney.js earnStage — the rule checkout uses, MassPay included).
export type EarnStage = "ready" | "waitingApproval" | "needsBank" | "frozen" | "declined";
// The paged /users response's count per stage, over every talent, plus how
// many talent without a bank got crowd requests in the last 30 days.
export type EarnStageCounts = Record<EarnStage, number> & { needsBankWithRequests?: number };

export interface NewTalentResult {
  user: { _id: string; name: string; email: string; slug: string; bankCountry: string };
  tempPassword: string;
  loginUrl: string;
}

export interface User {
  _id: string;
  name: string;
  email: string;
  role?: string;
  slug?: string;
  avatarUrl?: string;
  isFrozen?: boolean;
  freezeType?: "temporary" | "permanent" | null;
  freezeReason?: string | null;
  frozenAt?: string | null;
  frozenUntil?: string | null;
  isStripeOnboarded?: boolean;
  // THE IG GATE: approved = isActivated, declined = declinedAt (server
  // lib/approval.js; a decline is not a freeze since 1.5.1).
  isActivated?: boolean;
  declinedAt?: string | null;
  // What the talent sees in the app (server lib/approval.js). Absent from an
  // older server.
  approvalStatus?: "approved" | "in_review" | "needs_instagram" | "declined";
  // Absent from a server older than the "Ready to earn" chips.
  earnStage?: EarnStage;
  // Crowd requests in the last 30 days (free or paid; not tips, playlists or
  // the talent's own tests). `fans` counts fan accounts / devices, which free
  // requests only record from 2026-10-30. Absent = none.
  crowd30d?: { requests: number; fans: number; lastAt: string };
  instagram?: string | null;
  links?: string[];
  phoneNumber?: string | null;
  bankLast4?: string | null;
  bankBrand?: string | null;
  // Set when the talent was created by an admin (deferred-Stripe v1).
  // Used to badge the row as "Pre-Stripe" instead of the alarming red Stripe badge.
  createdByAdminId?: string | null;
}

export interface Order {
  _id: string;
  dj: string | { _id: string; name: string; avatarUrl?: string };
  searchQuery: string;
  amount: number;
  createdAt: string;
  status?: "pending" | "queued" | "accepted" | "declined" | "played";
  currency?: string;
  totalAmount?: number;
  djNetShare?: number;
  feeAmount?: number;
  paid?: boolean;
  trackImage?: string;
  trackArtists?: string[];
  trackAlbum?: string;
  customerEmail?: string;
}

export interface PayoutEvent {
  _id: string;
  batchId: string;
  createdAt: string;
  totalGross: number;
  totalNet: number;
  ordersCount: number;
  ordersAccepted: number;
  ordersDeclined: number;
  avgRequestValue: number;
  // Per-currency gross/fee/net from the server — drives per-currency display.
  totalsByCurrency?: Record<string, CurrencyTotals>;
}

// Feedback tab (components/AdminFeedbackTab.tsx).
// Mirrors models/Feedback.js. The snapshotted djName/djSlug/djEmail are the
// display source of truth (they survive a talent renaming or deleting their
// account); the populated `dj` ref is used only to link to the talent profile.
export interface FeedbackDj {
  _id: string;
  name?: string;
  email?: string;
  avatarUrl?: string;
  slug?: string;
  // A frozen talent's board posts are left off the board.
  isFrozen?: boolean;
}

export interface Feedback {
  _id: string;
  dj?: FeedbackDj | null;
  djName?: string | null;
  djSlug?: string | null;
  djEmail?: string | null;
  subject: string;
  message: string;
  // Screenshots + silent app context. Absent on older records.
  photos?: string[];
  appVersion?: string | null;
  appBuild?: string | null;
  platform?: string | null;
  deviceModel?: string | null;
  status?: "new" | "reviewed" | "archived";
  createdAt: string;
  // Feedback board (1.5.1 #10). `board` = posted from the board's "Share
  // feedback / Feature request"; `onBoard` = the post's own state allows it on
  // the board (not held, not cleared, under two reports or put back = `kept`);
  // a frozen author (`dj.isFrozen`) still keeps it off. Absent on rows from an
  // older server.
  board?: boolean;
  held?: boolean;
  clearedAt?: string | null;
  kept?: boolean;
  votes?: number;
  reportCount?: number;
  onBoard?: boolean;
}

// PATCH /admin/feedback/:id { cleared } answers the row's new board state.
export type FeedbackBoardState = Pick<Feedback, "_id" | "board" | "held" | "clearedAt" | "kept" | "votes" | "reportCount" | "onBoard">;

// Fans tab (components/AdminFansTab.tsx). Mirrors server lib/admin/fans.js:
// GET /admin/fans (the list) and GET /admin/fans/:id (one fan).
// The chips: every account / seen on the iPhone or Android app / never on it.
export type FanAppFilter = "all" | "app" | "web";

export interface AdminFanRow {
  _id: string;
  name: string | null;
  email: string | null;
  avatarUrl: string | null;
  instagram: string | null;
  provider: "google" | "apple" | "email";
  createdAt: string;
  // "ios" / "android". On the app ⇔ appLastSeenAt is set.
  appPlatforms: string[];
  appLastSeenAt: string | null;
  // Paid requests (tips aside); played and refunded count among them.
  requests: number;
  played: number;
  refunded: number;
  tips: number;
  // Paid, captured and kept (not refunded, not disputed) — the fan's card
  // total per currency, never converted.
  spentByCurrency: Record<string, number>;
  talentCount: number;
  lastRequestAt: string | null;
}

export interface AdminFanList {
  items: AdminFanRow[];
  // Rows matching the search AND the chip; counts match the search only.
  total: number;
  counts: Record<FanAppFilter, number>;
  // Platform-wide: accounts, created in the last 7 days, fans with a paid
  // order in the last 30 days, the share (0–1) of the last 30 days' paid
  // requests placed by a signed-in fan, and how the accounts sign in
  // (absent from a server older than the sign-in split).
  summary: {
    total: number;
    newThisWeek: number;
    active30d: number;
    signedInShare30d: number;
    providers?: Record<AdminFanRow["provider"], number>;
  };
}

// GET /api/admin/orders/:id/refund-preview — the refund confirm sheet: the
// exact figures, or why this request can't be refunded (server
// lib/orders/refunds.js refundPreviewFor; the talent's own refund rules).
export type AdminRefundPreview =
  | { refundable: true; fanTotal: number; djDrop: number; currency: string; cardFee: number | null }
  | { refundable: false; reason: string };

export interface AdminFanOrder {
  _id: string;
  createdAt: string;
  searchQuery: string;
  trackArtists: string[];
  amount: number;
  totalAmount: number | null;
  currency: string;
  status: "pending" | "queued" | "accepted" | "declined" | "played" | "refunded" | "frozen" | "expired";
  isTip: boolean;
  isPlaylist: boolean;
  paid: boolean;
  playedAt: string | null;
  refundedAt: string | null;
  // A played order with either flag is not money the fan kept paying.
  captureFailed: boolean;
  isDisputed: boolean;
  // null = the talent's account is gone.
  dj: { _id: string; name: string | null; slug: string | null } | null;
}

export interface AdminFanDetail {
  fan: AdminFanRow;
  // Their latest 100 orders, newest first (unpaid checkouts included).
  orders: AdminFanOrder[];
}

// Dashboard, Requests, Revenue and Trending tabs. Mirrors server
// lib/admin/stats.js: each tab asks for exactly what it shows, added up in
// the database. Money is a per-currency map, never converted or added across
// currencies. A talent line with id null is deleted talent ("Unknown").
export type AdminMoney = Record<string, number>;
export type AdminStatusMix = Record<"selected" | "pending" | "queued" | "accepted" | "declined" | "played", number>;

// GET /admin/stats/overview?since=
export interface AdminOverviewStats {
  since: string | null;
  kpis: {
    earningsByCurrency: AdminMoney;
    volumeByCurrency: AdminMoney;
    talentPayoutsByCurrency: AdminMoney;
    paidCount: number;
    // Paid orders with no date — counted on All Time only.
    undatedPaidCount: number;
    orderCount: number;
  };
  statusMix: AdminStatusMix;
  // Top 5 by revenue; barPct = share of the leader's total (0–100).
  topTalent: { id: string | null; name: string; avatarUrl: string | null; barPct: number; amountByCurrency: AdminMoney; count: number }[];
}

// GET /admin/stats/revenue?since=
export interface AdminRevenueStats {
  since: string | null;
  // In the order the tab lists them: currency of the newest paid order first.
  byCurrency: { currency: string; volume: number; fees: number; talentShare: number; count: number }[];
  undatedPaidCount: number;
  // Top 10 by revenue.
  topTalent: { id: string | null; name: string; avatarUrl: string | null; count: number; revenueByCurrency: AdminMoney; feeByCurrency: AdminMoney; avgByCurrency: AdminMoney }[];
}

// GET /admin/stats/trending?since=
export type TrendingSort = "total" | "paid" | "selected" | "revenue";
export type TrendingDir = "desc" | "asc";
// Each sort and direction's top 20, as positions in the songs / artists list.
export type TrendingRanks = Record<TrendingSort, Record<TrendingDir, number[]>>;
export interface AdminTrendingSong {
  query: string;
  artists: string[];
  image: string | null;
  paidCount: number;
  selectedCount: number;
  revenueByCurrency: AdminMoney;
}
export interface AdminTrendingArtist {
  name: string;
  paidCount: number;
  selectedCount: number;
  revenueByCurrency: AdminMoney;
}
export interface AdminTrendingStats {
  since: string | null;
  orderCount: number;
  songs: AdminTrendingSong[];
  songRanks: TrendingRanks;
  artists: AdminTrendingArtist[];
  artistRanks: TrendingRanks;
}

// GET /admin/requests — one Requests table row (the fields the table draws).
export interface AdminRequestRow {
  _id: string;
  dj: string | { _id: string; name: string; avatarUrl?: string };
  searchQuery: string;
  amount: number;
  createdAt: string;
  status?: "pending" | "queued" | "accepted" | "declined" | "played";
  currency?: string;
  trackImage?: string;
  trackArtists?: string[];
  trackAlbum?: string;
  totalAmount?: number;
  feeAmount?: number;
  djNetShare?: number;
  paid?: boolean;
  customerEmail?: string;
  customerName?: string | null;
  clientNickname?: string | null;
  clientInstagram?: string | null;
  cardLast4?: string | null;
  originalAmount?: number | null;
  counterAmount?: number | null;
  isPriority?: boolean;
  tier?: string;
  declineReason?: string | null;
  isDisputed?: boolean;
  // Phase 2.5: proactive track-history attachment at fulfillment time.
  trackHistoryStripeFileId?: string | null;
  trackHistoryFilename?: string | null;
  trackHistoryFileType?: "export" | "screenshot" | null;
  trackHistoryUploadedAt?: string | null;
}

// GET /admin/requests?status=&dj=&q=&from=&to=&missingTrackHistory=&sort=&limit=&skip=
// One page of rows; total and kpis cover EVERY matching order.
export interface AdminRequestsPage {
  items: AdminRequestRow[];
  total: number;
  kpis: { grossByCurrency: AdminMoney; feesByCurrency: AdminMoney; avgByCurrency: AdminMoney; completed: number };
}

// GET /admin/payouts/summary?since= — over EVERY payout (createdAt ≥ since
// when given). A payout with no per-currency breakdown counts as usd, from its
// totalGross / totalNet, like the All Payouts table shows it. undatedCount =
// payouts with no date (included when there is no `since`).
export interface AdminPayoutSummary {
  count: number;
  grossByCurrency: AdminMoney;
  netByCurrency: AdminMoney;
  undatedCount: number;
}

// GET /admin/disputes/summary — over EVERY dispute. needsResponse counts
// needs_response + warning_needs_response, underReview counts under_review +
// warning_under_review. amountByCurrency is in minor units (cents).
export interface AdminDisputeSummary {
  total: number;
  needsResponse: number;
  underReview: number;
  won: number;
  lost: number;
  amountByCurrency: AdminMoney;
}
